// components/tree/CollapsibleAlert.jsx
import { useState } from 'react';
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';

const CollapsibleAlert = ({
  severity = 'info',
  summary,
  children,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  // Dynamic icon per severity
  const iconMap = {
    error: <ErrorRoundedIcon color="error" />,
    warning: <WarningRoundedIcon color="warning" />,
    success: <CheckCircleRoundedIcon color="success" />,
    info: <InfoRoundedIcon color="info" />,
  };

  const colorMap = {
    error: 'error',
    warning: 'warning',
    success: 'success',
    info: 'info',
  };

  const getBaseColor = (theme) => {
    const paletteKey = colorMap[severity] || 'info';
    const entry = theme.palette[paletteKey];
    // fallback to info.main if something is missing
    return entry && entry.main ? entry.main : theme.palette.info.main;
  };

  return (
    <Box>
      {/* One-line “summary” row */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setOpen((prev) => !prev)}
        sx={(theme) => {
          const baseColor = getBaseColor(theme);
          return {
            cursor: 'pointer',
            userSelect: 'none',
            px: 1,
            py: 0.75,
            mb: 1,
            borderRadius: 1.5,
            borderLeft: `6px solid ${baseColor}`,
            bgcolor: alpha(baseColor, 0.08),
            '&:hover': {
              bgcolor: alpha(baseColor, 0.1),
            },
            transition: 'background-color 0.2s ease',
          };
        }}
      >
        {iconMap[severity]}

        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            fontWeight: 500,
            color: (theme) => theme.palette.text.primary,
          }}
        >
          {summary}
        </Typography>

        <IconButton
          size="small"
          disableRipple
          sx={(theme) => ({
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          })}
        >
          <ExpandMoreRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Collapsible details */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box>
          <Alert
            severity={severity}
            icon={false}
            sx={(theme) => {
              const baseColor = getBaseColor(theme);
              return {
                mb: 1,
                borderLeft: `6px solid ${baseColor}`,
                bgcolor: alpha(baseColor, 0.08),
              };
            }}
          >
            {children}
          </Alert>
        </Box>
      </Collapse>
    </Box>
  );
};

export default CollapsibleAlert;
